import { supabase } from './supabase'

/**
 * 139. Suspend or restore access for a group, from the selection bar.
 *
 * The server skips yourself and anyone who administers people (tenant_admin,
 * hr_admin) — those stay one at a time, in the panel — and requires a reason to
 * suspend. Each person is decided separately: one refusal is reported against
 * that name, it does not undo the rest.
 */
export type AccessOutcome =
  | 'suspended'
  | 'restored'
  | 'already_suspended'
  | 'already_active'
  | 'is_you'
  | 'administers_people'
  | 'not_a_member'
  /** 153: outside the caller's region; `detail` says where they sit. */
  | 'out_of_scope'
  | 'refused'

export type AccessResult = {
  personId: string
  displayName: string | null
  outcome: AccessOutcome
  detail: string | null
}

export async function setAccess(personIds: string[], active: boolean, reason?: string): Promise<AccessResult[]> {
  const { data, error } = await supabase.rpc('set_access', {
    p_person_ids: personIds,
    p_active: active,
    p_reason: reason?.trim() || null,
  })
  if (error) throw error
  return (
    (data ?? []) as { person_id: string; display_name: string | null; outcome: AccessOutcome; detail: string | null }[]
  ).map((r) => ({ personId: r.person_id, displayName: r.display_name, outcome: r.outcome, detail: r.detail }))
}

/** Why somebody in the selection was left as they were. */
export const ACCESS_OUTCOME_LABEL: Record<AccessOutcome, string> = {
  suspended: 'suspended',
  restored: 'restored',
  already_suspended: 'already suspended',
  already_active: 'already active',
  is_you: 'that is you',
  administers_people: 'an administrator — suspend them one at a time, from their panel',
  not_a_member: 'not in this company',
  out_of_scope: 'outside your region',
  refused: 'refused',
}

/**
 * 153. Why one person was left as they were, in words. An out-of-region row
 * carries where they sit in `detail`, which alone would read as an address.
 */
export function accessWhy(r: AccessResult): string {
  if (r.outcome === 'out_of_scope') {
    return r.detail ? `outside your region (${r.detail})` : ACCESS_OUTCOME_LABEL.out_of_scope
  }
  return r.detail ?? ACCESS_OUTCOME_LABEL[r.outcome]
}

/** True for the outcomes that changed nothing and need no explanation. */
export const isAccessDone = (o: AccessOutcome) => o === 'suspended' || o === 'restored'

/** "12 suspended · 2 already suspended · 1 left as they were" */
export function summariseAccess(results: AccessResult[], active: boolean): string {
  const done = results.filter((r) => isAccessDone(r.outcome)).length
  const already = results.filter((r) => r.outcome === (active ? 'already_active' : 'already_suspended')).length
  const rest = results.length - done - already
  const parts = [`${done} ${active ? 'restored' : 'suspended'}`]
  if (already) parts.push(`${already} already ${active ? 'active' : 'suspended'}`)
  if (rest) parts.push(`${rest} left as they were`)
  return parts.join(' · ')
}
